"use client"

import { useState, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import {
  Flag,
  FlagOff,
  Download,
  Printer,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Eye,
  X,
  FileText,
  Crown,
  Heart,
  Briefcase,
} from "lucide-react"
import { dateFormat_YY_MM_DY_timeStamp } from "../../../shared/utils/utils"

interface ILeadershipRatings {
  overallRating: number
  id: string
  rateeId: string
  dimension: string
  isflagged: boolean
  department: string
  description: string
  criteria: {
    [raterId: string]: {
      additionalComment?: string
      stopDoing: string
      startDoing: string
      continueDoing: string
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number
        }
      }
      leadershipScore?: number
    }
  }
  date: number
  raterId?: string
  templateId?: string
  heading?: string
  reasonForRequest?: string
  dueDate?: number
  values?: {}
  archived?: boolean
}

interface ModernRatingsTableProps {
  data: ILeadershipRatings[]
  description: string
}

const ModernRatingsTable = observer(({ data, description }: ModernRatingsTableProps) => {
  const { api, store } = useAppContext()
  const templates = store.templates.all.map((template) => template.asJson)

  // State management
  const [searchTerm, setSearchTerm] = useState("")
  const [filterDimension, setFilterDimension] = useState("all")
  const [filterFlagged, setFilterFlagged] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedRow, setSelectedRow] = useState<ILeadershipRatings | null>(null)
  const [showFlagDialog, setShowFlagDialog] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [loading, setLoading] = useState(false)

  // Get template title
  const getTemplateTitle = (templateId: string): string => {
    const foundTemplate = templates.find((template) => template.id === templateId)
    return foundTemplate ? foundTemplate.title : templateId
  }

  // Get dimension icon
  const getDimensionIcon = (dimension: string) => {
    if (dimension === "Values") return <Heart className="h-4 w-4 text-blue-600" />
    if (dimension === "Leadership") return <Crown className="h-4 w-4 text-purple-600" />
    return <Briefcase className="h-4 w-4 text-green-600" />
  }

  // Filter and search data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.dimension.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTemplateTitle(item.dimension).toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.values(item.criteria).some(
          (criteria) =>
            criteria.startDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.stopDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.continueDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.additionalComment?.toLowerCase().includes(searchTerm.toLowerCase()),
        )

      const matchesDimension =
        filterDimension === "all" ||
        item.dimension === filterDimension ||
        getTemplateTitle(item.dimension) === filterDimension

      const matchesFlagged =
        filterFlagged === "all" ||
        (filterFlagged === "flagged" && item.isflagged) ||
        (filterFlagged === "unflagged" && !item.isflagged)

      return matchesSearch && matchesDimension && matchesFlagged
    })
  }, [data, searchTerm, filterDimension, filterFlagged])

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

  // Get unique dimensions for filter
  const uniqueDimensions = useMemo(() => {
    const dimensions = Array.from(new Set(data.map((item) => item.dimension)))
    return dimensions.map((dim) => ({
      value: dim,
      label: dim === "Values" || dim === "Leadership" ? dim : getTemplateTitle(dim),
    }))
  }, [data])

  // Handle flag toggle
  const handleToggleFlag = async (row: ILeadershipRatings) => {
    setSelectedRow(row)
    setShowFlagDialog(true)
  }

  const confirmToggleFlag = async () => {
    if (!selectedRow) return

    setLoading(true)
    try {
      let updatedRating: any

      if (selectedRow.dimension === "Values") {
        const checkInstore = store.valueRating.getItemById(selectedRow.id)?.asJson
        if (checkInstore) {
          updatedRating = {
            ...checkInstore,
            isflagged: !selectedRow.isflagged,
            department: selectedRow.department,
            values: selectedRow.criteria,
          }
          await api.valueRating.update(updatedRating, updatedRating.rateeId, description)
        }
      } else if (selectedRow.dimension === "Leadership") {
        const checkInstore = store.leadershipRating.getItemById(selectedRow.id)?.asJson
        if (checkInstore) {
          updatedRating = {
            ...checkInstore,
            isflagged: !selectedRow.isflagged,
            department: selectedRow.department,
            criteria: selectedRow.criteria,
          }
          await api.leadershipRating.update(updatedRating, updatedRating.rateeId, description)
        }
      } else {
        updatedRating = {
          ...selectedRow,
          isflagged: !selectedRow.isflagged,
          department: selectedRow.department,
        }
        await api.templateRating.update(updatedRating)
      }

      setShowFlagDialog(false)
      setSelectedRow(null)
    } catch (error) {
      console.error("Error updating rating:", error)
    } finally {
      setLoading(false)
    }
  }

  // Handle view details
  const handleViewDetails = (row: ILeadershipRatings) => {
    setSelectedRow(row)
    setShowDetailsModal(true)
  }

  // Export functions (placeholder)
  const handleExportPDF = () => {
    console.log("Export PDF functionality")
  }

  const handlePrint = () => {
    console.log("Print functionality")
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        await api.templates.getAll()
        await api.value.getAll()
        await api.leadership.getAll()
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }
    fetchData()
  }, [])

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search ratings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <select
            value={filterDimension}
            onChange={(e) => setFilterDimension(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Dimensions</option>
            {uniqueDimensions.map((dim) => (
              <option key={dim.value} value={dim.value}>
                {dim.label}
              </option>
            ))}
          </select>

          <select
            value={filterFlagged}
            onChange={(e) => setFilterFlagged(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="flagged">Flagged Only</option>
            <option value="unflagged">Unflagged Only</option>
          </select>

          {/* Export buttons */}
          <button
            onClick={handlePrint}
            className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="Print"
          >
            <Printer className="h-5 w-5" />
          </button>
          <button
            onClick={handleExportPDF}
            className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="Export PDF"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length}{" "}
          ratings
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value))
            setCurrentPage(1)
          }}
          className="px-2 py-1 border border-slate-300 rounded text-sm"
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Dimension
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Start Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Stop Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Continue Doing
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedData.map((row, index) => {
                const criteria =
                  row.criteria && Object.keys(row.criteria).length > 0
                    ? row.criteria[Object.keys(row.criteria)[0]]
                    : null

                return (
                  <tr
                    key={row.id}
                    className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-25"}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-900">{dateFormat_YY_MM_DY_timeStamp(row.date)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getDimensionIcon(row.dimension)}
                        <span className="text-sm font-medium text-slate-900">
                          {row.dimension === "Values" || row.dimension === "Leadership"
                            ? row.dimension
                            : getTemplateTitle(row.dimension)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.startDoing || ""}>
                        {criteria?.startDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.stopDoing || ""}>
                        {criteria?.stopDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.continueDoing || ""}>
                        {criteria?.continueDoing || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {row.isflagged ? (
                          <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                            <AlertTriangle className="h-3 w-3" />
                            Flagged
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                            <CheckCircle2 className="h-3 w-3" />
                            Normal
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(row)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleFlag(row)}
                          className={`p-2 rounded-lg transition-colors ${
                            row.isflagged
                              ? "text-red-600 hover:text-red-800 hover:bg-red-50"
                              : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                          }`}
                          title={row.isflagged ? "Unflag Rating" : "Flag Rating"}
                        >
                          {row.isflagged ? <Flag className="h-4 w-4" /> : <FlagOff className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {paginatedData.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No ratings found</h3>
            <p className="text-slate-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Flag Confirmation Dialog */}
      {showFlagDialog && selectedRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${selectedRow.isflagged ? "bg-green-100" : "bg-red-100"}`}>
                {selectedRow.isflagged ? (
                  <FlagOff className="h-5 w-5 text-green-600" />
                ) : (
                  <Flag className="h-5 w-5 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedRow.isflagged ? "Unflag Rating" : "Flag Rating"}
              </h3>
            </div>
            <p className="text-slate-600 mb-6">
              Are you sure you want to {selectedRow.isflagged ? "unflag" : "flag"} this rating? This action will change
              its status.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowFlagDialog(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmToggleFlag}
                disabled={loading}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  selectedRow.isflagged ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                } disabled:opacity-50`}
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {getDimensionIcon(selectedRow.dimension)}
                <h3 className="text-xl font-semibold text-slate-900">Rating Details</h3>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Date</label>
                  <p className="text-slate-900">{dateFormat_YY_MM_DY_timeStamp(selectedRow.date)}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Dimension</label>
                  <p className="text-slate-900">
                    {selectedRow.dimension === "Values" || selectedRow.dimension === "Leadership"
                      ? selectedRow.dimension
                      : getTemplateTitle(selectedRow.dimension)}
                  </p>
                </div>
              </div>

              {/* Feedback Details */}
              {Object.keys(selectedRow.criteria).length > 0 && (
                <div className="space-y-4">
                  {Object.entries(selectedRow.criteria).map(([raterId, criteria]) => (
                    <div key={raterId} className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 mb-3">Feedback</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-semibold text-green-700">Start Doing</label>
                          <p className="text-slate-900">{criteria.startDoing || "No feedback provided"}</p>
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-red-700">Stop Doing</label>
                          <p className="text-slate-900">{criteria.stopDoing || "No feedback provided"}</p>
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-blue-700">Continue Doing</label>
                          <p className="text-slate-900">{criteria.continueDoing || "No feedback provided"}</p>
                        </div>
                        {criteria.additionalComment && (
                          <div>
                            <label className="text-sm font-semibold text-slate-700">Additional Comments</label>
                            <p className="text-slate-900">{criteria.additionalComment}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default ModernRatingsTable
